import type React from "react";
import {
	type Dispatch,
	type SetStateAction,
	type ReactNode,
	useCallback,
	useState,
} from "react";

import { roomIdSelector } from "@redux/room/selectors";
import * as Sentry from "@sentry/browser";
import throttle from "lodash/throttle";
import { useSelector } from "react-redux";
import type { Song } from "src/@types/api-types";

import { manualTimeUpdateEvent } from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.constants";
import SharedPlayerArea from "components/shared/SharedPlayerArea";
import { useMediaElementListener } from "services/hooks/useMediaElementListener";
import type { SCOPE } from "shared-types/constants";
import {
	PlayerEvent,
	type PlayerEventsListeners,
	type PlayerSongDetails,
	type SendPlayerEvent,
} from "shared-types/player-event";

const SEND_CURRENT_TIME_FREQUENCY_IN_MS = 1000;

interface PlayerAreaEventsWrapperProps {
	// We pass an object here instead of directly the songId as a string
	// to trigger a rerender when the currentSong is updated, even when
	// the songId is the same: this is useful when playing twice the same
	// song
	song?: { id: string } | null;
	activeVideoElement: HTMLVideoElement | null;
	setInstrumentalVideoElement: Dispatch<
		SetStateAction<HTMLVideoElement | null>
	>;
	setVocalGuideVideoElement?: Dispatch<SetStateAction<HTMLVideoElement | null>>;
	handlePlay: () => void;
	handleVideoEnd: () => void;
	sendPlayerEvent: SendPlayerEvent;
	usePlayerEventsListener: (
		eventListeners: PlayerEventsListeners,
		castingSessionId?: string,
	) => void;
	scope: SCOPE;
	castingSessionId?: string;
	playButton?: ReactNode;
	controls?: boolean;
	waitingList: Song[];
	isStorageActivated: boolean;
}

const PlayerAreaEventsWrapper: React.FunctionComponent<
	PlayerAreaEventsWrapperProps
> = ({
	song,
	activeVideoElement,
	setInstrumentalVideoElement,
	setVocalGuideVideoElement,
	handlePlay,
	handleVideoEnd,
	sendPlayerEvent,
	usePlayerEventsListener,
	scope,
	castingSessionId,
	playButton,
	controls = true,
	waitingList,
	isStorageActivated,
}) => {
	const [currentSongDetails, setCurrentSongDetails] =
		useState<PlayerSongDetails | null>(null);
	const [nextSongDetails, setNextSongDetails] =
		useState<PlayerSongDetails | null>(null);
	const roomId = useSelector(roomIdSelector);

	useMediaElementListener(activeVideoElement, "play", () => {
		sendPlayerEvent(PlayerEvent.ON_PLAY);
	});

	useMediaElementListener(activeVideoElement, "pause", () => {
		sendPlayerEvent(PlayerEvent.ON_PAUSE);
	});

	useMediaElementListener(activeVideoElement, "durationchange", () => {
		if (activeVideoElement) {
			sendPlayerEvent(
				PlayerEvent.DURATION,
				activeVideoElement.duration.toString(),
			);
		}
	});

	const throttleSendCastinSignal = useCallback(
		throttle(() => {
			if (!activeVideoElement) return;
			sendPlayerEvent(
				PlayerEvent.CURRENT_TIME,
				activeVideoElement.currentTime.toString(),
			);
		}, SEND_CURRENT_TIME_FREQUENCY_IN_MS),
		[activeVideoElement, sendPlayerEvent],
	);
	useMediaElementListener(
		activeVideoElement,
		"timeupdate",
		throttleSendCastinSignal,
	);

	usePlayerEventsListener(
		{
			[PlayerEvent.PAUSE]: () => activeVideoElement?.pause(),
			[PlayerEvent.PLAY]: handlePlay,
			[PlayerEvent.SET_TIME]: (event: MessageEvent) => {
				if (activeVideoElement) {
					const time = Number.parseInt(event.data, 10);
					if (!Number.isNaN(time)) {
						activeVideoElement.currentTime = time;
						activeVideoElement.dispatchEvent(manualTimeUpdateEvent);
					}
				}
			},
			[PlayerEvent.CURRENT_SONG_DETAILS]: (event: MessageEvent) => {
				try {
					const songDetails = event.data && JSON.parse(event.data);
					setCurrentSongDetails(
						songDetails?.title && songDetails.artistName ? songDetails : null,
					);
				} catch (error) {
					Sentry.captureException(
						new Error(
							`Error setting current song detail ${error.message}, event data ${event.data}`,
						),
						{
							tags: {
								roomId,
							},
						},
					);
				}
			},
			[PlayerEvent.NEXT_SONG_DETAILS]: (event: MessageEvent) => {
				const songDetails = event.data && JSON.parse(event.data);
				setNextSongDetails(
					songDetails?.title && songDetails.artistName ? songDetails : null,
				);
			},
		},
		castingSessionId,
	);

	return (
		<SharedPlayerArea
			song={song}
			activeVideoElement={activeVideoElement}
			setInstrumentalVideoElement={setInstrumentalVideoElement}
			setVocalGuideVideoElement={setVocalGuideVideoElement}
			handlePlay={handlePlay}
			handleVideoEnd={() => {
				handleVideoEnd();
				sendPlayerEvent(PlayerEvent.SONG_END);
			}}
			playButton={playButton}
			controls={controls}
			scope={scope}
			currentSongDetails={currentSongDetails}
			nextSongDetails={nextSongDetails}
			waitingList={waitingList}
			isStorageActivated={isStorageActivated}
		/>
	);
};

export default PlayerAreaEventsWrapper;
